import { useEffect, useRef } from "react";
import { toast } from "sonner";

// Controlla se in produzione è live una versione dell'app più recente di quella
// attualmente caricata nel browser (utile durante/dopo un deploy, anche al primo
// accesso servito da una copia CDN datata). Se rileva una versione nuova mostra
// un avviso non invasivo con pulsante "Ricarica". Non fa nulla in dev (asset-manifest
// assente) e non ricarica mai da solo per evitare loop.
const POLL_MS = 90 * 1000;

function loadedMainFile() {
  try {
    const src = [...document.scripts]
      .map((s) => s.src || "")
      .find((s) => /\/static\/js\/main\.[^/]+\.js/.test(s));
    if (!src) return null;
    const m = src.match(/main\.[^/]+\.js/);
    return m ? m[0] : null;
  } catch {
    return null;
  }
}

async function latestMainFile() {
  try {
    const res = await fetch(`/asset-manifest.json?ts=${Date.now()}`, {
      cache: "no-store",
    });
    if (!res.ok) return null;
    const data = await res.json();
    const main = data?.files?.["main.js"];
    if (main) {
      const m = String(main).match(/main\.[^/]+\.js/);
      return m ? m[0] : null;
    }
    const ep = (data?.entrypoints || []).find((f) => /main\.[^/]+\.js/.test(f));
    if (ep) {
      const m = ep.match(/main\.[^/]+\.js/);
      return m ? m[0] : null;
    }
    return null;
  } catch {
    return null;
  }
}

export function VersionChecker() {
  const notified = useRef(false);

  useEffect(() => {
    const loaded = loadedMainFile();
    if (!loaded) return; // dev server / impossibile determinare la versione: no-op

    let cancelled = false;

    const check = async () => {
      if (cancelled || notified.current) return;
      const latest = await latestMainFile();
      if (!latest || cancelled || notified.current) return;
      if (latest !== loaded) {
        notified.current = true;
        toast("È disponibile una nuova versione", {
          description: "Ricarica la pagina per aggiornare all'ultima versione.",
          duration: Infinity,
          action: {
            label: "Ricarica",
            onClick: () => window.location.reload(),
          },
        });
      }
    };

    check();
    const id = setInterval(check, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);

    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", check);
    };
  }, []);

  return null;
}

export default VersionChecker;
